/**
 * Чтение системных значений из атрибутов предмета: серийный номер, город, юр. лицо.
 *
 * Единственная точка, где фронт решает, «какой атрибут считать городом». Таблица и карточка
 * зовут systemAttributeValue() с семантическим ключом и о названиях справочников не знают —
 * иначе списки написаний разошлись бы между списком и карточкой, и одно и то же оборудование
 * показывало бы разные города.
 *
 * ВРЕМЕННЫЙ СЛОЙ СОВМЕСТИМОСТИ. Связь «название справочника → смысл» существует потому,
 * что у типа атрибута нет системного ключа: сущности «тип атрибута» в базе нет вовсе,
 * справочник — это набор строк inventory_attributes с одинаковым type. Когда появится
 * system_key (шаг 6.2), списки названий уходят целиком, а SYSTEM_ATTRIBUTE остаётся —
 * его значения намеренно совпадают с будущими ключами.
 *
 * Это слой чтения старых данных, а не бизнес-правило «все эти типы — одно поле». Фильтрация,
 * экспорт и интеграции на него опираться не должны.
 */

/**
 * Семантические ключи системных атрибутов. Значения совпадают с планируемыми system_key.
 */
export const SYSTEM_ATTRIBUTE = {
    SERIAL_NUMBER: 'serial_number',
    LOCATION_CITY: 'location_city',
    LEGAL_ENTITY: 'legal_entity',
};

/**
 * Написания типов по убыванию приоритета: первым идёт каноническое название.
 *
 * Порядок здесь — это правило выбора, а не просто перечисление. Если у предмета есть
 * и «Город», и «Города», побеждает «Город»: канонический тип всегда старше legacy,
 * независимо от того, в каком порядке база вернула строки атрибутов.
 */
const TYPE_PRIORITY = {
    [SYSTEM_ATTRIBUTE.LOCATION_CITY]: ['город', 'города'],
    [SYSTEM_ATTRIBUTE.LEGAL_ENTITY]: ['юр. лицо', 'юр лицо', 'юр. лица', 'юр лица', 'юрлицо', 'юрлица'],
    [SYSTEM_ATTRIBUTE.SERIAL_NUMBER]: ['серийный номер'],
};

/**
 * ДОЛГ. Серийный номер, не совпавший с каноническим названием, ищется по подстроке:
 * вариантов написания много («Серийные номера», «серийник»), перечислить все не выйдет.
 * Первая буква отброшена — она бывает и заглавной, и строчной.
 *
 * Подстрока поймает и чужой справочник, если это сочетание окажется в его названии.
 * Уходит вместе с остальным слоем, когда у типа появится system_key.
 */
const SERIAL_NUMBER_FALLBACK_PART = 'ерийн';

/**
 * Прочерк для отсутствующего значения — один на весь модуль, чтобы пустота в списке
 * и в карточке выглядела одинаково.
 */
export const EMPTY_VALUE = '—';

function normalizeType(attribute) {
    return String(attribute?.type ?? '').toLowerCase();
}

function attributesOf(item) {
    return Array.isArray(item?.attributes) ? item.attributes : [];
}

/**
 * Первый атрибут с точно таким типом.
 *
 * Если под одним типом у предмета оказалось несколько значений — берётся первое по порядку
 * загрузки. Это вырожденный случай (справочник предполагает одно значение на предмет),
 * и разрешать его угадыванием нечем.
 *
 * @param {object} item
 * @param {string} type Нормализованное название типа.
 * @returns {object|null}
 */
function findByExactType(item, type) {
    return attributesOf(item).find((attribute) => normalizeType(attribute) === type) ?? null;
}

/**
 * Атрибут, отвечающий за системный смысл, с учётом приоритета написаний.
 *
 * @param {object} item
 * @param {string} key Значение из SYSTEM_ATTRIBUTE.
 * @returns {object|null}
 */
/**
 * Означает ли НАЗВАНИЕ типа этот системный смысл.
 *
 * Отличается от resolveSystemAttribute() тем, с чем работает: тот ищет атрибут у готового
 * предмета, а этот отвечает про голое название типа, когда предмета ещё нет. Нужен форме
 * создания: она должна понять, какой из атрибутов шаблона — серийный номер (его переносить
 * нельзя, он индивидуален), и в какой справочник положить введённый номер.
 *
 * Зеркало InventoryAttributeResolver::typeTitleMatches() на бэкенде — с той разницей,
 * что канонический справочник типов фронту недоступен, и решение принимается по написанию.
 * Последнее слово всё равно за сервером.
 *
 * @param {string} type Название типа.
 * @param {string} key Значение из SYSTEM_ATTRIBUTE.
 * @returns {boolean}
 */
export function isSystemAttributeType(type, key) {
    const normalized = String(type ?? '')
        .trim()
        .toLowerCase();

    if (normalized === '') {
        return false;
    }

    if ((TYPE_PRIORITY[key] ?? []).indexOf(normalized) !== -1) {
        return true;
    }

    // Тот же запасной путь, что и у чтения: написаний серийного номера много,
    // перечислить все не выйдет. См. комментарий к SERIAL_NUMBER_FALLBACK_PART.
    return key === SYSTEM_ATTRIBUTE.SERIAL_NUMBER && normalized.includes(SERIAL_NUMBER_FALLBACK_PART);
}

/**
 * Системный смысл названия типа или null.
 *
 * @param {string} type
 * @returns {string|null}
 */
export function systemAttributeKeyOf(type) {
    const keys = [SYSTEM_ATTRIBUTE.SERIAL_NUMBER, SYSTEM_ATTRIBUTE.LOCATION_CITY, SYSTEM_ATTRIBUTE.LEGAL_ENTITY];

    return keys.find((key) => isSystemAttributeType(type, key)) ?? null;
}

/**
 * Смыслы, которым в разделе справочников делать нечего (7-D3).
 *
 * ГОРОД — не справочник оборудования. Канонический источник у него один:
 * inventory_items.city_id со ссылкой на общий справочник городов. Строка «Город»
 * среди значений EAV — это второй источник того же факта, и живёт он только потому,
 * что так заводили до 6.2. Показывать его в конструкторе значит предлагать завести
 * расхождение своими руками.
 *
 * СЕРИЙНЫЙ НОМЕР — свойство конкретной единицы, а не перечень допустимых значений.
 * «Справочник серийных номеров» — это список всех номеров всей техники, заполняемый
 * по одному значению на единицу; заводить его вручную нечем и незачем.
 *
 * ЮР. ЛИЦО ЗДЕСЬ НЕТ намеренно: это настоящий справочник — конечный список компаний,
 * который администратор ведёт руками и выбирает селектом в форме создания.
 *
 * Это правило ПОКАЗА, а не чтения. Накопленные значения обоих смыслов остаются в базе
 * и продолжают читаться (resolveSystemAttribute, itemCityName): скрыть форму заведения
 * и удалить данные — разные вещи, и второго здесь не происходит.
 */
export const HIDDEN_DICTIONARY_KEYS = [SYSTEM_ATTRIBUTE.LOCATION_CITY, SYSTEM_ATTRIBUTE.SERIAL_NUMBER];

/**
 * Прячется ли справочник с таким названием от пользовательского раздела справочников.
 *
 * @param {string} type Название типа (справочника).
 * @returns {boolean}
 */
export function isHiddenDictionaryType(type) {
    return HIDDEN_DICTIONARY_KEYS.some((key) => isSystemAttributeType(type, key));
}

/**
 * Список справочников без тех, что заведению не подлежат.
 *
 * @param {Array} types Строки со свойством type.
 * @returns {Array}
 */
export function withoutHiddenDictionaries(types) {
    const list = Array.isArray(types) ? types : [];

    return list.filter((entry) => !isHiddenDictionaryType(entry?.type));
}

export function resolveSystemAttribute(item, key) {
    const types = TYPE_PRIORITY[key] ?? [];

    for (const type of types) {
        const found = findByExactType(item, type);

        if (found) {
            return found;
        }
    }

    if (key !== SYSTEM_ATTRIBUTE.SERIAL_NUMBER) {
        return null;
    }

    // Канонического названия нет — ищем по подстроке. Совпадений может быть несколько
    // («Серийный номер оборудования», «Серийник»), поэтому сортируем по названию типа:
    // выбор должен быть один и тот же при каждом открытии карточки, а порядок строк
    // из базы этого не гарантирует.
    const matches = attributesOf(item)
        .filter((attribute) => normalizeType(attribute).includes(SERIAL_NUMBER_FALLBACK_PART))
        .sort((left, right) => normalizeType(left).localeCompare(normalizeType(right)));

    return matches[0] ?? null;
}

/**
 * Значение системного атрибута или прочерк.
 *
 * Значение возвращается как есть: справочники задвоены, и склеивать два «Ижевска»
 * из разных справочников — работа по нормализации данных, а не задача отображения.
 *
 * @param {object} item
 * @param {string} key Значение из SYSTEM_ATTRIBUTE.
 * @returns {string}
 */
export function systemAttributeValue(item, key) {
    const attribute = resolveSystemAttribute(item, key);

    return attribute && attribute.value ? attribute.value : EMPTY_VALUE;
}

/**
 * Город единицы техники.
 *
 * Канонический источник — собственное поле предмета item.city (inventory_items.city_id
 * со ссылкой на общий справочник cities). Атрибут «Город»/«Города» остаётся только
 * запасным вариантом для позиций, которым город ещё не проставили: массового переноса
 * старых данных не было и до отдельного шага не будет.
 *
 * Город ответственного здесь не участвует ни при каких условиях. У предмета на складе
 * город есть, а ответственного нет вовсе, и подставлять чужой город значило бы выдать
 * догадку за учётные данные.
 *
 * @param {object} item
 * @returns {string}
 */
export function itemCityName(item) {
    const canonical = item?.city?.name;

    if (canonical) {
        return canonical;
    }

    return systemAttributeValue(item, SYSTEM_ATTRIBUTE.LOCATION_CITY);
}

/**
 * Атрибуты, не показанные в основном блоке карточки.
 *
 * Системные выведены отдельными полями, и повторять их в «Дополнительных атрибутах»
 * значит показывать одно значение дважды. Скрывается именно выбранная строка:
 * если у предмета есть и «Город», и «Города», второй останется в дополнительных —
 * он в карточке не показан, и прятать его было бы потерей данных.
 *
 * @param {object} item
 * @returns {object[]}
 */
export function extraAttributes(item) {
    // Скрывается только та строка, значение которой реально показано выше.
    //
    // Для города это теперь условие, а не данность. Если город взят из item.city,
    // то legacy-атрибут «Город» источником показанного значения НЕ является — и может
    // с ним расходиться: в поле «Ижевск» из справочника, в атрибуте «Казань» с прошлых
    // времён. Спрятать такую строку значило бы сделать расхождение невидимым, а данные —
    // молча потерянными. Поэтому при заполненном item.city атрибут города остаётся
    // в дополнительных, где его видно и можно сверить.
    //
    // Отдельного интерфейса конфликтов для этого не нужно: достаточно не прятать.
    const hiddenKeys = [SYSTEM_ATTRIBUTE.SERIAL_NUMBER, SYSTEM_ATTRIBUTE.LEGAL_ENTITY];

    if (!item?.city?.name) {
        hiddenKeys.push(SYSTEM_ATTRIBUTE.LOCATION_CITY);
    }

    const shownIds = hiddenKeys
        .map((key) => resolveSystemAttribute(item, key))
        .filter(Boolean)
        .map((attribute) => attribute.id);

    return attributesOf(item).filter((attribute) => shownIds.indexOf(attribute.id) === -1);
}
